import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCampaigns, type CampaignView } from '../api/campaigns';
import { fetchChangelog, type ChangelogRelease } from '../api/changelog';
import { fetchMissions, type MissionCard } from '../api/missions';
import { fetchProfile, type ProfileView } from '../api/profiles';
import { fetchServers } from '../api/servers';
import { fetchSessions, type SessionRow } from '../api/sessions';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { Avatar } from './IdentityPanel';
import { MissionCover } from './MissionCover';
import { useMeta } from './MetaContext';
import { PageProblem } from './PageStates';
import { useRetryKey } from './connectionHelpers';
import { TesterStart } from './TesterStart';
import { Icon, Shell } from './Shell';

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ca', { dateStyle: 'full', timeStyle: 'short' }) : null;

const short = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ca', { dateStyle: 'medium', timeStyle: 'short' }) : null;

/** How many of the latest version's changes the home's news block lists before «I N canvis més». */
const NEWS_SHOWN = 8;

/** A stub or an older platform can answer anything; only a real array is a list. */
const list = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : []);

/** LIVE first, then the soonest scheduled; the undated ones last. */
function byWhen(a: SessionRow, b: SessionRow): number {
  if (a.state !== b.state) return a.state === 'LIVE' ? -1 : 1;
  return (a.scheduledAt ?? '9').localeCompare(b.scheduledAt ?? '9');
}

/** The chip a night's end is drawn with, as the sessions page draws it. */
const outcomeTone = (outcome: string) =>
  outcome === 'success'
    ? 'ok'
    : outcome === 'failure' || outcome === 'failed'
      ? 'bad'
      : outcome === 'partial'
        ? 'warn'
        : '';

/** The hours this account played since the first of this month, tests left out; null when nothing says. */
function hoursThisMonth(profile: ProfileView | null, now: Date): number | null {
  if (!profile || !Array.isArray(profile.matches)) return null;
  const first = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const seconds = profile.matches
    .filter((m) => !m.test && m.startedAt && new Date(m.startedAt).getTime() >= first)
    .reduce((sum, m) => sum + (m.secondsPlayed ?? 0), 0);
  return seconds / 3600;
}

/**
 * Home (UI doc section 4, `/`): the page a member lands on, and the one thing it leads with is
 * the next thing to do — the night being played, to join; else the next one scheduled, with how
 * long is left; else nothing, said in one line. Under it: this account's numbers, each a door to
 * the page behind it; the group's missions with their own pictures; the nights this account
 * played; and what changed in the platform lately.
 *
 * Every figure comes from a call the app already made somewhere: nothing new is asked of the
 * platform. There is no account card: the rail has the account, on every page. There is no
 * signed-out half either: `AuthGate` sends whoever has no session to the sign-in page first.
 */
export function Home() {
  const { meta, loaded, error } = useMeta();
  const auth = useAuth();
  const me = auth.me;
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [missions, setMissions] = useState<MissionCard[] | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignView[] | null>(null);
  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [releases, setReleases] = useState<ChangelogRelease[] | null>(null);
  const [serversUp, setServersUp] = useState<number | null>(null);
  const [clock, setClock] = useState(() => Date.now());
  /** The sessions or the missions did not arrive: an empty list would read as "there are none". */
  const [failure, setFailure] = useState<unknown>(null);
  const [attempt, retry] = useRetryKey();
  const canSeeServers = me.roles.includes('ADMIN') || me.roles.includes('SERVER_MANAGER');
  const canCreate = me.roles.includes('ADMIN') || me.roles.includes('EDITOR');
  const handle = me.handle ?? null;

  useEffect(() => {
    if (!me.authenticated) return;
    setFailure(null);
    fetchSessions()
      .then((answer) => setSessions(list(answer?.rows)))
      .catch((err) => {
        setFailure(err);
        setSessions([]);
      });
    fetchMissions()
      .then((answer) => setMissions(list(answer)))
      .catch((err) => {
        setFailure(err);
        setMissions([]);
      });
    fetchCampaigns()
      .then((answer) => setCampaigns(list(answer)))
      .catch(() => setCampaigns([]));
    fetchChangelog()
      .then((answer) => setReleases(list(answer)))
      .catch(() => setReleases([]));
  }, [me.authenticated, attempt]);

  // The profile is where the platform counts this account's nights without the tests, and where
  // the time played is. A page without it still has a number: the rows of the list above.
  useEffect(() => {
    if (!me.authenticated || !handle) return;
    fetchProfile(handle)
      .then((answer) => setProfile(answer && typeof answer === 'object' ? answer : null))
      .catch(() => setProfile(null));
  }, [me.authenticated, handle]);

  useEffect(() => {
    if (!me.authenticated || !canSeeServers) return;
    fetchServers()
      .then((r) => setServersUp(list(r?.servers).filter((s) => s.status?.alive).length))
      .catch(() => setServersUp(null));
  }, [me.authenticated, canSeeServers]);

  // A tests' row never leads the page, whatever the list was asked for.
  const nights = useMemo(() => list(sessions).filter((r) => !r.test && !r.smoke), [sessions]);
  const upcoming = useMemo(() => nights.filter((r) => r.state !== 'ENDED').sort(byWhen), [nights]);
  const now = upcoming[0] ?? null;
  const later = upcoming.slice(1, 4);
  const live = now?.state === 'LIVE';
  const startsAt = now && !live ? now.scheduledAt : null;

  // The countdown moves by itself, twice a minute, only while there is one to move.
  useEffect(() => {
    if (!startsAt) return;
    const timer = setInterval(() => setClock(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [startsAt]);

  // The group's missions, tests left out as the library leaves them out; the ones nobody has
  // played yet first, then the most recently worked on.
  const toPlay = useMemo(() => {
    const played = (m: MissionCard) => list(m.sessions).some((s) => s.state === 'ENDED');
    return list(missions)
      .filter((m) => !m.test)
      .sort(
        (a, b) =>
          Number(played(a)) - Number(played(b)) ||
          (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''),
      );
  }, [missions]);
  const lastPlayed = useMemo(
    () => nights.filter((r) => r.state === 'ENDED' && r.played).slice(0, 5),
    [nights],
  );
  const active = list(campaigns).filter((c) => c.state !== 'FINISHED');
  const playedCount = profile?.sessionsPlayed ?? nights.filter((r) => r.played).length;
  const hours = hoursThisMonth(profile, new Date(clock));
  // The latest version that has something to say; what is still unreleased only when nothing else is.
  const latest =
    list(releases).find((r) => !r.unreleased && list(r.entries).length > 0) ??
    list(releases).find((r) => list(r.entries).length > 0) ??
    null;

  // A stranger never sees this page: `AuthGate` sends whoever has no session to the sign-in page.
  if (!auth.loaded || !me.authenticated) return null;

  const when = (row: SessionRow) => fmt(row.scheduledAt ?? row.startedAt) ?? ca.home.undated;
  const t = ca.home;

  return (
    <Shell
      title={t.welcome(me.displayName ?? me.handle ?? '')}
      actions={
        <>
          {canCreate && (
            <Link className="button primary" to="/new">
              {t.newMission}
            </Link>
          )}
          <Link className="button" to="/sessions">
            {t.allSessions}
          </Link>
        </>
      }
    >
      <TesterStart where="home" />
      {failure !== null && <PageProblem error={failure} onRetry={retry} />}
      <div className="dashboard home">
        <section
          className={`card hero ${now ? 'has-night' : 'quiet'} ${live ? 'live' : ''}`}
          aria-label={t.now}
        >
          {sessions === null && <p className="muted small">{ca.loading}</p>}
          {sessions !== null && !now && failure === null && (
            <div className="hero-empty">
              <Icon name="calendar-x" />
              <div className="grow">
                <p>{t.noSession}</p>
                <p className="muted small">{canCreate ? t.noSessionHint : t.noSessionHintPlayer}</p>
              </div>
              {canCreate && (
                <Link className="button primary" to="/new">
                  {t.newMission}
                </Link>
              )}
            </div>
          )}
          {now && (
            <>
              {(now.missionCode || now.worldName) && (
                <MissionCover
                  code={now.missionCode}
                  revision={now.missionRevision}
                  world={now.worldName}
                  worldLabel={now.worldDisplayName ?? now.worldName}
                  recipe={now.recipe}
                  recipeIcon={now.recipeIcon}
                  recipeTitle={now.recipeTitle}
                  cover={now.cover ?? 'AUTO'}
                  className="hero-cover"
                  size="card"
                />
              )}
              <span className="hero-shade" aria-hidden="true" />
              <div className="row chips">
                <span className={`chip state ${live ? 'live' : ''}`}>
                  {live && <span className="pulse" aria-hidden="true" />}
                  {live ? t.live : t.nextSession}
                </span>
                {now.worldName && (
                  <span className="chip">{now.worldDisplayName ?? now.worldName}</span>
                )}
                {startsAt && (
                  <span className="chip countdown">
                    <i className="bi bi-hourglass-split" aria-hidden="true" />{' '}
                    {t.countdown(new Date(startsAt).getTime() - clock)}
                  </span>
                )}
              </div>
              <h2>{now.missionTitle}</h2>
              <p className="hero-facts small">
                {[
                  when(now),
                  now.serverName ? t.onServer(now.serverName) : null,
                  now.playersOnline !== null && now.playersOnline !== undefined
                    ? t.playersOnline(now.playersOnline)
                    : live
                      ? t.playersOnline(now.playerCount)
                      : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <div className="row actions">
                {now.code && live && (
                  <Link className="button primary" to={`/s/${now.code}`}>
                    <i className="bi bi-box-arrow-in-right" aria-hidden="true" />
                    {t.join}
                  </Link>
                )}
                {now.code && (
                  <Link
                    className={`button ${live ? '' : 'primary'}`}
                    to={`/s/${now.code}/briefing`}
                  >
                    <i className="bi bi-journal-text" aria-hidden="true" />
                    {t.briefing}
                  </Link>
                )}
                {now.code && !live && (
                  <Link className="button" to={`/s/${now.code}`}>
                    {t.openSession}
                  </Link>
                )}
                {now.missionCode && (
                  <Link className="button ghost" to={`/m/${now.missionCode}`}>
                    {t.openMission}
                  </Link>
                )}
              </div>
              {later.length > 0 && (
                <>
                  <h3>{t.later}</h3>
                  <ul className="list">
                    {later.map((row) => (
                      <li key={`${row.kind}-${row.code ?? row.missionTitle}-${row.scheduledAt}`}>
                        <span className="grow">
                          <strong>{row.missionTitle}</strong>{' '}
                          <span className="muted small">{short(row.scheduledAt) ?? t.undated}</span>
                        </span>
                        {row.code && (
                          <Link className="button small" to={`/s/${row.code}`}>
                            {t.openSession}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </section>

        <nav className="stats links" aria-label={t.figures}>
          <Link className="stat" to="/sessions">
            <span className="label">{t.statPlayed}</span>
            <span className="value">{sessions === null && !profile ? '—' : playedCount}</span>
          </Link>
          {hours !== null && handle && (
            <Link className="stat" to={`/u/${handle}`}>
              <span className="label">{t.statHours}</span>
              <span className="value">{t.hours(hours)}</span>
            </Link>
          )}
          <Link className="stat" to="/library">
            <span className="label">{t.statMissions}</span>
            <span className="value">{missions === null ? '—' : toPlay.length}</span>
          </Link>
          {canSeeServers && (
            <Link className="stat" to="/servers">
              <span className="label">{t.statServers}</span>
              <span className="value">{serversUp === null ? '—' : serversUp}</span>
            </Link>
          )}
        </nav>

        <section className="card wide" aria-label={t.toPlay}>
          <div className="row between">
            <h2>{t.toPlay}</h2>
            <Link className="button small" to="/library">
              {t.allMissions}
            </Link>
          </div>
          {missions === null && <p className="muted small">{ca.loading}</p>}
          {missions !== null && toPlay.length === 0 && (
            <div className="empty">
              <Icon name="collection" />
              <p>{t.noMissions}</p>
              <p className="muted small">{canCreate ? t.noMissionsHint : t.noMissionsHintPlayer}</p>
            </div>
          )}
          {toPlay.length > 0 && (
            <div className="cards grid">
              {toPlay.slice(0, 6).map((m) => (
                <Link key={m.code} className="card mission" to={`/m/${m.code}`}>
                  <div className="thumb-wrap">
                    <MissionCover
                      code={m.code}
                      revision={m.revision}
                      world={m.worldName}
                      worldLabel={m.worldDisplayName ?? m.worldName}
                      recipe={m.recipe}
                      recipeIcon={m.recipeIcon}
                      recipeTitle={m.recipeTitle ?? m.recipe}
                      cover={m.cover ?? 'AUTO'}
                    />
                    {list(m.sessions).some((x) => x.state === 'LIVE') && (
                      <span className="chip state live">
                        <span className="pulse" aria-hidden="true" />
                        {t.live}
                      </span>
                    )}
                  </div>
                  <div className="body">
                    <strong>{m.title}</strong>
                    <span className="muted small">
                      {[
                        m.worldDisplayName ?? m.worldName,
                        m.playerSlots ? ca.reportPlayers(m.playerSlots) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    <span className="row author">
                      {m.ownerHandle && (
                        <>
                          <Avatar url={m.ownerAvatarUrl} name={m.ownerHandle} size={20} />
                          <span className="muted small">{ca.by(m.ownerHandle)}</span>
                        </>
                      )}
                      {m.recipeTitle && (
                        <span className="chip">
                          <i className={`bi bi-${m.recipeIcon || 'flag'}`} aria-hidden="true" />{' '}
                          {m.recipeTitle}
                        </span>
                      )}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="card" aria-label={t.lastPlayed}>
          <div className="row between">
            <h2>{t.lastPlayed}</h2>
            <Link className="button small" to="/sessions">
              {t.allSessions}
            </Link>
          </div>
          {sessions === null && <p className="muted small">{ca.loading}</p>}
          {sessions !== null && lastPlayed.length === 0 && (
            <p className="muted small">{t.noPlayed}</p>
          )}
          {lastPlayed.length > 0 && (
            <ul className="list">
              {lastPlayed.map((row) => (
                <li key={`${row.kind}-${row.code ?? row.reportId ?? row.missionTitle}`}>
                  <span className="grow">
                    {row.code ? (
                      <Link to={`/s/${row.code}`}>{row.missionTitle}</Link>
                    ) : (
                      <strong>{row.missionTitle}</strong>
                    )}{' '}
                    <span className="muted small num">
                      {[
                        short(row.startedAt ?? row.endedAt),
                        row.playerCount ? ca.reportPlayers(row.playerCount) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {row.outcome && (
                    <span className={`chip ${outcomeTone(row.outcome)}`}>
                      {ca.outcomes[row.outcome] ?? row.outcome}
                    </span>
                  )}
                  {row.reportUrl && (
                    <a
                      className="button small"
                      href={row.reportUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t.reportOf(row.missionTitle)}
                    >
                      <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                      {t.report}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {latest && (
          <section className="card news" aria-label={t.news}>
            <div className="row between">
              <h2>{t.news}</h2>
              <span className="muted small">
                {latest.unreleased
                  ? ca.changelogPage.unreleased
                  : ca.changelogPage.version(latest.version)}
              </span>
            </div>
            <ul className="list">
              {latest.entries.slice(0, NEWS_SHOWN).map((entry) => (
                <li key={entry.key}>
                  <i
                    className={`bi bi-${ca.changelogPage.icons[entry.kind] ?? 'stars'}`}
                    aria-hidden="true"
                    title={ca.changelogPage.kinds[entry.kind] ?? entry.kind}
                  />
                  <span className="grow">{entry.title}</span>
                </li>
              ))}
              {latest.entries.length > NEWS_SHOWN && (
                <li className="news-more">
                  <Link className="muted small" to="/novetats">
                    {t.moreNews(latest.entries.length - NEWS_SHOWN)}
                  </Link>
                </li>
              )}
            </ul>
            <div className="row">
              <Link className="button small" to="/novetats">
                {t.allNews}
              </Link>
            </div>
          </section>
        )}

        {active.length > 0 && (
          <section className="card" aria-label={t.campaigns}>
            <h2>{t.campaigns}</h2>
            <ul className="list">
              {active.slice(0, 5).map((c) => (
                <li key={c.code}>
                  <span className="grow">
                    <Link to={`/c/${c.code}`}>{c.title}</Link>{' '}
                    <span className="muted small">{c.worldDisplayName ?? c.worldName}</span>
                  </span>
                  <span className="chip">{ca.chapter(c.chapter)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="muted small home-status">
          {!loaded && ca.loading}
          {loaded && !error && ca.version(meta.version)}
          {loaded && error && ca.platformDown}
          {loaded && !error && !meta.panelConfigured && <> · {ca.noPanel}</>}
        </p>
      </div>
    </Shell>
  );
}
